// Utility function to determine whether a given URL belongs to the current domain (local) or is external

// Usage ==> isLocalUrl(slide.URL);
export function isLocalUrl(urlToCheck) {
  return urlToCheck?.includes(SITEBASEURL) ?? false;
}

// ****************************************************************************************************************************************************
// Utility function to detect window size against a defined breakpoint size

// Usage ==> validateWindowSize(800);

export function validateWindowSize(breakpointValue) {
  const breakpoint = globalThis.matchMedia(`(min-width: ${breakpointValue}px)`);
  return breakpoint.matches;
}

// ****************************************************************************************************************************************************
// Utility function to add attributes to links based on whether they are local, external, or empty

function getLinkType(url) {
  if (!url) return 'empty';
  if (isLocalUrl(url)) return 'local';
  return 'external';
}

function getAttributesString(urlToCheck, placeholder) {
  const type = getLinkType(urlToCheck);

  switch (type) {
    case 'empty':
      return placeholder
        ? `aria-label="${placeholder}" aria-disabled="true"`
        : `aria-disabled="true"`;

    case 'local':
      return placeholder ? `aria-label="${placeholder}"` : '';

    case 'external': {
      const label = placeholder ? `aria-label="${placeholder} (opens in a new tab)"` : '';
      return [`target="_blank"`, `rel="noopener noreferrer"`, label].filter(Boolean).join(' ');
    }
  }
}

function setAttributesDOM($el, urlToCheck, placeholder) {
  const type = getLinkType(urlToCheck);

  switch (type) {
    case 'empty':
      $el.removeAttr('href').addClass('void-link');
      $el.attr('aria-disabled', 'true');
      if (placeholder) $el.attr('aria-label', placeholder);
      break;

    case 'local':
      if (placeholder) $el.attr('aria-label', placeholder);
      break;

    case 'external':
      $el.attr({ target: '_blank', rel: 'noopener noreferrer' });
      if (placeholder) $el.attr('aria-label', `${placeholder} (opens in a new tab)`);
      break;
  }
}

export function updateLinksAttributes(urlToCheck, placeholder, $el = null) {
  if ($el) {
    setAttributesDOM($el, urlToCheck, placeholder);
    return;
  }

  return getAttributesString(urlToCheck, placeholder);
}

// ****************************************************************************************************************************************************
// Utility function to update card link behavior based on widget data (replaces modifyLinkBehavior + validateLinks)

export function updateCardLinkBehavior(widgetData, $cards, isMobileBreakpoint = false) {
  $cards.each(function (itemIndex) {
    const $card = $(this);
    const $buttons = $card.find('.buttonwrap');
    const cardIsLink = $card.is('a');

    // Extract URL from widget data (PurchaseURL takes precedence over URL)
    const itemData = widgetData?.items?.[itemIndex] || {};
    const urlToCheck = itemData.PurchaseURL || itemData.URL || '';

    // Update link attributes based on URL type
    updateLinksAttributes(urlToCheck, '', $card);

    // Handle mobile-specific behavior
    if (isMobileBreakpoint && cardIsLink && $buttons.length) {
      $buttons.remove();
    }
  });
}

// ****************************************************************************************************************************************************
// Utility function to update accessibility attributes for Swiper slides and their content

export function updateSlideA11y(swiper) {
  const $slides = $(swiper.slides);

  $slides.attr({
    'aria-selected': 'false',
    'aria-hidden': 'true',
    tabindex: '-1',
  });

  $slides.find('a, button').attr('tabindex', '-1');

  const $active = $slides.filter('.swiper-slide-active');

  $active.removeAttr('inert').attr({
    'aria-selected': 'true',
    'aria-hidden': 'false',
    tabindex: '0',
  });

  $active.find('a, button').attr('tabindex', '0');
}

export function updateImageA11y(swiper) {
  const $slides = $(swiper.slides);

  $slides.each(function (_, slide) {
    const $slide = $(slide);
    const isActive = $slide.hasClass('swiper-slide-active');

    $slide.attr('aria-hidden', isActive ? 'false' : 'true');
  });
}

// ****************************************************************************************************************************************************
// Makes any non-button element keyboard and screen-reader accessible as a button

// Usage ==> makeAccessibleButton($('.my-toggle'), 'Open menu', () => { ... });

export function makeAccessibleButton($el, label, onActivate) {
  if (!$el.length) return;

  if (!$el.is('button')) {
    $el.attr({
      tabindex: 0,
      role: 'button',
      'aria-label': label,
    });
  }

  $el.off('keydown.accessibleBtn').on('keydown.accessibleBtn', function (e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      $(this).trigger('click');
    }
  });

  if (onActivate) {
    $el.off('click.accessibleBtn').on('click.accessibleBtn', onActivate);
  }
}

// ****************************************************************************************************************************************************
// Sets a CSS custom property (--theme-header-height) that tracks the header's rendered height via ResizeObserver

// Usage ==> observeHeaderHeight('.header');

export function observeHeaderHeight(selector = 'header', { offset = 0 } = {}) {
  const header = document.querySelector(selector);
  if (!header || !('ResizeObserver' in globalThis)) return { destroy() {} };

  let lastHeight = null;

  function apply() {
    const h = Math.round(header.getBoundingClientRect().height) + offset;

    if (h !== lastHeight) {
      lastHeight = h;
      document.documentElement.style.setProperty('--theme-header-height', `${h}px`);
    }
  }

  const ro = new ResizeObserver(apply);
  ro.observe(header);
  apply();

  return { destroy: () => ro.disconnect() };
}

// ****************************************************************************************************************************************************
// CMS patch: normalizes the search box in the header

export function modifySearchBox() {
  const searchInput = $('.header .inputFieldTextbox');
  const searchButton = $('.header .searchSubmitButton');

  searchInput.attr('placeholder', 'Type here...');
  searchButton.attr('tabindex', '-1');
  searchButton.attr('aria-label', 'submit search');
}

// ****************************************************************************************************************************************************
// Spark patch: collapses the Feature Lineup button row when social icons + CTA overflow the container width

export function modifyFeatureLineup() {
  const lineups = $('.featureLineupContainer');
  if (!lineups.length) return;

  lineups.each((_, el) => {
    const $lineup = $(el);
    const containerWidth = $lineup.width();
    const buttonContainer = $lineup.find('.featureLineup-buttons.subContainer');
    const icons = buttonContainer.find('.featureLineup-social');
    const cta = buttonContainer.find('.featureLineup-buynow');

    if (!icons.children().length) {
      icons.remove();
      return;
    }

    const totalWidth = icons.outerWidth(true) + cta.outerWidth(true);

    if (totalWidth > containerWidth) {
      buttonContainer.addClass('column');
    }
  });
}
